// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class HashUtil {
  // simple hash from public domain https://github.com/bryc/code/blob/fc8b79d7d8b91118e1846fd01e9d08d4d07d0f6a/jshash/experimental/cyrb53.js
  public static cyrb53(str: string, seed = 0) {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;
    for (let i = 0, ch; i < str.length; i += 1) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }

  /**
   * Validate that the signed score is recent and matches the security algo (the hash must end with 'ff')
   * Signed scores are sent via `signedScore` in the request and has the format `${toSign}#${sig}`.
   *
   * The timeout is used to see if the signature is too old. The timestamp of when the the `toSign` is set must be
   * first element in the array of `${toSign}.split('-')`.
   */
  public static validateSignedScore({
    toSign,
    sig,
    log,
    timeoutSeconds,
  }: {
    toSign: string;
    sig: string;
    timeoutSeconds: number;
    log?: (_msg: string) => void;
  }): boolean {
    const bits = toSign.split('-');
    if (bits.length !== 5) {
      log?.(`Invalid signature format: - ${toSign}`);
      return false;
    }
    const now = Date.now();
    const dt = Math.abs(now - parseInt(bits[0] ?? '', 10));
    if (dt > 1000 * timeoutSeconds) {
      // Users clocks are too often wrong to count on this for security, so we just log it.
      // The csrf token has a timestamp we do validate
      log?.(`Old signature dt: ${dt} - ${toSign}`);
    }

    const hash = HashUtil.cyrb53(toSign + sig).toString(16);
    const endsWith = hash.endsWith('ff');
    if (!endsWith) {
      log?.(`Invalid signature hash: ${hash} - ${toSign}`);
      return false;
    }

    return true;
  }

  /** Generate the value of `toSign` for validateSignedSignature  */
  public static generateToSign({ csrfToken }: { csrfToken: string }): string {
    return `${Date.now().toString()}-${csrfToken}-${HashUtil.makeSalt()}`;
  }

  /** Generate a random hex string */
  public static makeSalt(): string {
    const salt = Math.floor(Math.random() * (1 << 30)).toString(16);
    return HashUtil.cyrb53(`${salt}${Date.now()}`).toString(16);
  }
}
